import React from 'react';

const CHANNELS = [
  {
    key: 'offline',
    label: 'Offline Marketing',
    subtitle: 'Print, Billboards, Flyers, Standees, Banners, Hoardings',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
    color: '#f59e0b',
  },
  {
    key: 'socialMedia',
    label: 'Social Media Posts',
    subtitle: 'Instagram, Facebook, LinkedIn, X, YouTube — scheduled via Postiz',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
      </svg>
    ),
    color: '#3b82f6',
  },
  {
    key: 'onlineAds',
    label: 'Online Ads',
    subtitle: 'Meta Ads, Google Ads, LinkedIn Ads — with targeting, CTAs, ROAS tracking',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
      </svg>
    ),
    color: '#10b981',
    comingSoon: true,
  },
];

const ChannelSelectionModal = ({ strategy, isDarkMode, selectedChannels, onToggle, onContinue, onBack }) => {
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const textMuted = isDarkMode ? 'text-gray-500' : 'text-gray-400';
  const bubbleBg = isDarkMode ? 'bg-gray-800' : 'bg-gray-200';
  const borderColor = isDarkMode ? 'border-gray-700' : 'border-gray-200';

  // Check if budget is "No Budget" — disables Online Ads channel
  const isNoBudget = strategy?.budgetRange === 'No Budget' || strategy?.budgetAllocation === 'No Budget';

  // Get recommended allocation from strategy
  const getRecommended = (key) => {
    if (!strategy?.channelStrategy) return null;
    const ch = strategy.channelStrategy.find(c =>
      (c.channel || c.platform || '').toLowerCase().includes(
        key === 'offline' ? 'offline' : key === 'onlineAds' ? 'online' : 'social'
      )
    );
    return ch?.allocation || null;
  };

  return (
    <div className="flex items-start gap-2 mb-4">
      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm ${isDarkMode
        ? 'bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/30'
        : 'bg-gradient-to-br from-cyan-100 to-blue-100 border border-cyan-300'
      }`}>
        <svg className="w-4 h-4 text-cyan-500" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 0L13.5 8.5L22 10L13.5 11.5L12 20L10.5 11.5L2 10L10.5 8.5L12 0Z" />
        </svg>
      </div>

      <div className="flex-1 min-w-0 max-w-[90%] md:max-w-[80%]">
        <p className={`text-xs font-semibold mb-1.5 ml-1 ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>
          ✨ AI Marketing Strategist — Select Channels
        </p>

        <div className={`px-4 py-4 rounded-2xl shadow-sm ${bubbleBg} ${isDarkMode ? 'border border-gray-700/50' : ''}`}>
          <div className="mb-4">
            <h4 className={`text-sm font-bold ${textPrimary}`}>Select Execution Channels</h4>
            <p className={`text-xs ${textMuted} mt-1`}>
              Based on your strategy, choose which channels to generate content for. You can select multiple.
            </p>
            {isNoBudget && (
              <p className={`text-xs mt-2 px-2 py-1.5 rounded-lg font-medium ${isDarkMode ? 'bg-yellow-500/15 text-yellow-400 border border-yellow-500/30' : 'bg-yellow-50 text-yellow-700 border border-yellow-200'}`}>
                ⚠️ Budget set to <b>No Budget</b>. Online Ads is disabled. Only organic channels (Social Media, Offline Marketing) are available.
              </p>
            )}
          </div>

          {/* Channel cards */}
          <div className="space-y-3 mb-4">
            {CHANNELS.map(ch => {
              const isSelected = selectedChannels.includes(ch.key);
              const recommended = getRecommended(ch.key);
              const isComingSoon = !!ch.comingSoon;
              const isDisabled = isComingSoon || (ch.key === 'onlineAds' && isNoBudget);

              return (
                <button
                  key={ch.key}
                  onClick={() => !isDisabled && onToggle(ch.key)}
                  disabled={isDisabled}
                  className={`w-full flex items-start gap-3 p-3 rounded-xl border-2 transition-all duration-200 text-left ${isDisabled
                    ? 'opacity-50 cursor-not-allowed border-gray-600'
                    : isSelected
                      ? 'border-cyan-500 shadow-sm'
                      : isDarkMode
                        ? `border-gray-700 hover:border-gray-600 ${bubbleBg}`
                        : `border-gray-200 hover:border-gray-300 bg-white`
                  }`}
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors`}
                    style={{
                      backgroundColor: isSelected ? `${ch.color}22` : isDarkMode ? '#374151' : '#f3f4f6',
                      color: isSelected ? ch.color : isDarkMode ? '#9ca3af' : '#6b7280',
                    }}
                  >
                    {ch.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-bold ${isDisabled ? textMuted : textPrimary}`}>{ch.label}</span>
                      {isSelected && !isDisabled && (
                        <svg className="w-4 h-4 text-cyan-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                      {isComingSoon && (
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${isDarkMode ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30' : 'bg-purple-50 text-purple-600 border border-purple-200'}`}>
                          Coming Soon
                        </span>
                      )}
                      {!isComingSoon && isDisabled && (
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${isDarkMode ? 'bg-gray-700 text-gray-400 border border-gray-600' : 'bg-gray-100 text-gray-500 border border-gray-200'}`}>
                          Disabled — No Budget
                        </span>
                      )}
                    </div>
                    <p className={`text-xs ${isDisabled ? textMuted : textSecondary} mt-0.5`}>{ch.subtitle}</p>
                    {recommended && !isDisabled && (
                      <p className={`text-xs mt-1 font-medium`} style={{ color: ch.color }}>
                        Recommended budget: {recommended}
                      </p>
                    )}
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-1 ${isSelected && !isDisabled
                    ? 'border-cyan-500 bg-cyan-500'
                    : isDisabled
                      ? 'border-gray-600 bg-gray-800'
                      : isDarkMode ? 'border-gray-600' : 'border-gray-300'
                  }`}>
                    {isSelected && !isDisabled && (
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Action buttons */}
          <div className="flex gap-2">
            <button
              onClick={onContinue}
              disabled={selectedChannels.length === 0}
              className="flex-1 py-2.5 rounded-xl text-white text-sm font-bold transition-all duration-200 flex items-center justify-center gap-2 active:scale-[0.98] hover:shadow-lg hover:shadow-cyan-500/25 hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: 'linear-gradient(135deg, #06b6d4, #3b82f6)' }}
            >
              <span>Continue →</span>
            </button>
            <button
              onClick={onBack}
              className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 active:scale-[0.98] ${isDarkMode
                ? 'bg-gray-700/60 text-gray-300 hover:bg-gray-700 border border-gray-600'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200'
              }`}
            >
              Back to Strategy
            </button>
          </div>

          <div className="flex items-center mt-2 px-1">
            <span className={`text-[10px] ${textMuted}`}>⚡ Powered by Infinium</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChannelSelectionModal;